import React from 'react';
import { Copy, Check, Plus, Pencil, Trash2, Eye, EyeOff, RefreshCw, AlertTriangle, ChevronDown, X } from 'lucide-react';

import { settingsApi, shippingCompaniesApi } from '../lib/settingsApi';
import { toastSuccess, toastError } from '../lib/toast';
import { Switch } from '../components/ui/switch';
import {
    AlertDialog,
    AlertDialogContent,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogAction,
    AlertDialogCancel,
} from '../components/ui/alert-dialog';
import ShippingCompanyDialog from '../components/settings/ShippingCompanyDialog';

/**
 * /parametres — super_admin only (route already guarded by AppLayout).
 *
 * Two cards:
 *  1. "Connexion Shopify": webhook URL (readonly + copy), last order date,
 *     health badge, editable threshold input.
 *  2. "Transporteurs": table with credentials MASKED by default (points),
 *     eye icon per row → GET /credentials → shows for 15 s then re-masks.
 *
 * The Sheet detail order shipping dropdown is fed from /api/orders/filters
 * (unchanged, all authenticated roles). No regression here.
 */
function frMonth(m0) {
    return ['janv.', 'févr.', 'mars', 'avr.', 'mai', 'juin', 'juil.', 'août', 'sept.', 'oct.', 'nov.', 'déc.'][m0];
}
function formatFR(iso) {
    if (!iso) return '—';
    try {
        const d = new Date(iso);
        const pad = (n) => String(n).padStart(2, '0');
        return `${d.getDate()} ${frMonth(d.getMonth())} ${d.getFullYear()}, ${pad(d.getHours())}:${pad(d.getMinutes())}`;
    } catch { return iso; }
}

// -----------------------------------------------------------------------
// Phase 11.4 — Shopify webhook diagnostic card
// Rendered under the "Connexion Shopify" card. Collapsed by default.
// Surfaces the non-sensitive diagnostic bag returned by
// GET /api/settings/webhook-diagnostics: secret shape metadata (length,
// hex-purity, "would validate without the last char" hint), timestamp of
// the last valid HMAC hit, ring buffer of the last 10 HMAC failures.
// Never displays the secret itself.
// -----------------------------------------------------------------------
function DiagLine({ label, value, tone }) {
    const toneCls = tone === 'ok' ? 'text-[#10B981]'
                  : tone === 'warn' ? 'text-[#F59E0B]'
                  : tone === 'err' ? 'text-[#EF4444]'
                  : 'text-[#1A1D2E]';
    return (
        <div className="flex items-baseline justify-between gap-4 text-sm">
            <span className="text-[#6B7280]">{label}</span>
            <span className={`font-mono text-[13px] ${toneCls}`}>{value}</span>
        </div>
    );
}

function ShopifyDiagnosticCard({ diag, loading, open, onToggle, onRefresh }) {
    const s = diag?.secret;
    const failures = diag?.recent_failures || [];
    const showSuspectBanner = s && s.configured && (
        s.would_be_valid_if_last_char_removed === true
        || failures.some(f => f.reason === 'alt_secret_would_match')
    );
    return (
        <div
            data-testid="shopify-diag-card"
            className="bg-white rounded-xl border border-[#E5E7EB] shadow-[0_1px_2px_rgba(16,24,40,0.05)]"
        >
            <button
                type="button"
                data-testid="shopify-diag-toggle"
                onClick={onToggle}
                className="w-full flex items-center justify-between p-5 text-left"
            >
                <div className="flex items-center gap-3">
                    <h3 className="text-base font-semibold text-[#1A1D2E]">
                        Diagnostic webhook Shopify
                    </h3>
                    {showSuspectBanner && (
                        <span
                            data-testid="shopify-diag-suspect-badge"
                            className="inline-flex items-center gap-1.5 h-6 px-2.5 rounded-full text-[11px] font-semibold bg-[#EF4444]/15 text-[#EF4444]"
                        >
                            <AlertTriangle size={12} />
                            Anomalie détectée
                        </span>
                    )}
                </div>
                <ChevronDown
                    size={18}
                    className={`text-[#6B7280] transition-transform ${open ? 'rotate-180' : ''}`}
                />
            </button>

            {open && (
                <div className="px-5 pb-5 space-y-4">
                    <div className="flex items-center justify-between">
                        <p className="text-xs text-[#6B7280]">
                            Informations non sensibles. La clé n'est jamais affichée.
                        </p>
                        <button
                            type="button"
                            data-testid="shopify-diag-refresh"
                            onClick={onRefresh}
                            disabled={loading}
                            className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md text-[12px] font-medium text-[#1A1D2E] bg-white border border-[#E5E7EB] hover:border-[#C7C1F2] hover:text-[#4B3FD8] disabled:opacity-60 transition-colors"
                        >
                            <RefreshCw size={12} className={loading ? 'animate-spin' : ''} />
                            Rafraîchir
                        </button>
                    </div>

                    {diag?.error && (
                        <div data-testid="shopify-diag-error" className="text-sm text-[#EF4444]">{diag.error}</div>
                    )}

                    {s && s.configured && (
                        <div className="rounded-lg border border-[#E5E7EB] p-4 space-y-2">
                            <div className="text-[12px] font-semibold text-[#6B7280] uppercase tracking-wide mb-2">
                                Clé de signature configurée
                            </div>
                            <DiagLine
                                label="Longueur"
                                value={s.length}
                                tone={s.length === 64 ? 'ok' : 'warn'}
                            />
                            <DiagLine
                                label="Format 100 % hexadécimal"
                                value={s.is_pure_hex ? 'oui' : 'non'}
                                tone={s.is_pure_hex ? 'ok' : 'warn'}
                            />
                            <DiagLine
                                label="Attendu (64 hex purs)"
                                value={s.expected_length_64_hex_pure ? 'oui' : 'non'}
                                tone={s.expected_length_64_hex_pure ? 'ok' : 'err'}
                            />
                            {s.would_be_valid_if_last_char_removed === true && (
                                <div className="mt-3 rounded-md bg-[#EF4444]/10 border border-[#EF4444]/30 p-3 text-[13px] text-[#7A1919]" data-testid="shopify-diag-suspect-hint">
                                    <b>Caractère parasite en fin de clé.</b> La clé actuelle fait {s.length} caractères. Sans le dernier caractère (code {s.last_char_ord}), elle deviendrait exactement 64 caractères hexadécimaux — c'est le format Shopify. Retirez ce caractère dans <code className="font-mono">.env</code>.
                                </div>
                            )}
                        </div>
                    )}
                    {s && !s.configured && (
                        <div className="rounded-md bg-[#EF4444]/10 border border-[#EF4444]/30 p-3 text-[13px] text-[#7A1919]">
                            Aucune clé de signature Shopify configurée.
                        </div>
                    )}

                    <div className="rounded-lg border border-[#E5E7EB] p-4">
                        <div className="text-[12px] font-semibold text-[#6B7280] uppercase tracking-wide mb-2">
                            Dernier webhook valide reçu
                        </div>
                        <div data-testid="shopify-diag-last-success" className="text-sm text-[#1A1D2E]">
                            {diag?.last_hmac_success_at
                                ? formatFR(diag.last_hmac_success_at)
                                : <span className="text-[#EF4444]">Jamais depuis le dernier redémarrage.</span>}
                        </div>
                    </div>

                    <div className="rounded-lg border border-[#E5E7EB] overflow-hidden">
                        <div className="p-4 border-b border-[#E5E7EB] flex items-center justify-between">
                            <div className="text-[12px] font-semibold text-[#6B7280] uppercase tracking-wide">
                                {failures.length} échec{failures.length > 1 ? 's' : ''} HMAC récent{failures.length > 1 ? 's' : ''}
                            </div>
                            <span className="text-[11px] text-[#6B7280]">buffer {diag?.buffer_capacity || 10}</span>
                        </div>
                        {failures.length === 0 ? (
                            <div className="p-4 text-sm text-[#6B7280]">Aucun échec récent.</div>
                        ) : (
                            <div className="overflow-x-auto">
                                <table data-testid="shopify-diag-failures" className="w-full text-[13px]">
                                    <thead>
                                        <tr className="bg-[#F5F6FA] text-[11px] uppercase tracking-wider text-[#6B7280]">
                                            <th className="px-4 py-2 text-left">Horodatage</th>
                                            <th className="px-4 py-2 text-left">Topic</th>
                                            <th className="px-4 py-2 text-left">Boutique</th>
                                            <th className="px-4 py-2 text-left">HMAC</th>
                                            <th className="px-4 py-2 text-left">Taille</th>
                                            <th className="px-4 py-2 text-left">Raison</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {failures.slice().reverse().map((f, i) => (
                                            <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-[#F5F6FA]'}>
                                                <td className="px-4 py-2 font-mono text-[12px]">{formatFR(f.ts)}</td>
                                                <td className="px-4 py-2">{f.topic}</td>
                                                <td className="px-4 py-2 font-mono text-[12px]">{f.shop}</td>
                                                <td className="px-4 py-2">{f.hmac_header_present ? 'présent' : 'absent'}</td>
                                                <td className="px-4 py-2 font-mono text-[12px]">{f.body_bytes} o</td>
                                                <td className={`px-4 py-2 ${f.reason === 'alt_secret_would_match' ? 'text-[#EF4444] font-semibold' : ''}`}>
                                                    {f.reason === 'alt_secret_would_match' ? 'Clé avec caractère parasite'
                                                     : f.reason === 'no_hmac_header' ? 'En-tête HMAC absent'
                                                     : f.reason === 'bad_signature' ? 'Signature invalide'
                                                     : f.reason === 'secret_not_configured' ? 'Clé non configurée'
                                                     : f.reason}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

export default function ParametresPage() {
    // Settings card state
    const [settings, setSettings] = React.useState(null);
    const [thresholdInput, setThresholdInput] = React.useState('');
    const [thresholdSaving, setThresholdSaving] = React.useState(false);
    const [settingsLoading, setSettingsLoading] = React.useState(false);
    const [settingsError, setSettingsError] = React.useState(null);

    // Phase 11.4 — Shopify webhook diagnostics card
    const [diag, setDiag] = React.useState(null);
    const [diagLoading, setDiagLoading] = React.useState(false);
    const [diagOpen, setDiagOpen] = React.useState(false);

    // Shipping card state
    const [companies, setCompanies] = React.useState([]);
    const [shippingLoading, setShippingLoading] = React.useState(false);
    const [shippingError, setShippingError] = React.useState(null);
    const [revealed, setRevealed] = React.useState({}); // { [id]: {api_key, api_secret} }
    const [revealing, setRevealing] = React.useState(null); // id in progress
    const [dialogOpen, setDialogOpen] = React.useState(false);
    const [dialogMode, setDialogMode] = React.useState('create');
    const [dialogCompany, setDialogCompany] = React.useState(null);
    const [confirmDelete, setConfirmDelete] = React.useState(null);
    const [busy, setBusy] = React.useState(false);

    const loadSettings = React.useCallback(async () => {
        setSettingsLoading(true);
        setSettingsError(null);
        try {
            const s = await settingsApi.get();
            setSettings(s);
            setThresholdInput(String(s.webhook.threshold_minutes));
        } catch (e) {
            setSettingsError(e.message);
        } finally {
            setSettingsLoading(false);
        }
    }, []);

    const loadShipping = React.useCallback(async () => {
        setShippingLoading(true);
        setShippingError(null);
        try {
            const res = await shippingCompaniesApi.list();
            setCompanies(res.shipping_companies || []);
        } catch (e) {
            setShippingError(e.message);
        } finally {
            setShippingLoading(false);
        }
    }, []);

    React.useEffect(() => { loadSettings(); loadShipping(); }, [loadSettings, loadShipping]);

    // Phase 11.4 — load diagnostics on mount + explicit refresh button.
    const loadDiag = React.useCallback(async () => {
        setDiagLoading(true);
        try {
            const d = await settingsApi.getDiagnostics();
            setDiag(d);
        } catch (e) {
            // Non-blocking — the card just stays empty if diagnostics fail.
            setDiag({ error: e.message || 'Diagnostic indisponible.' });
        } finally {
            setDiagLoading(false);
        }
    }, []);
    React.useEffect(() => { loadDiag(); }, [loadDiag]);

    // ---- webhook card ---------------------------------------------------------
    const copyWebhookUrl = async () => {
        const url = settings?.webhook?.url;
        if (!url) return;
        try {
            await navigator.clipboard.writeText(url);
            toastSuccess('URL copiée dans le presse-papier.');
        } catch {
            toastError("Impossible de copier — vérifiez les permissions du navigateur.");
        }
    };

    const saveThreshold = async () => {
        const n = parseInt(thresholdInput, 10);
        if (!Number.isFinite(n)) {
            toastError('Valeur invalide.');
            return;
        }
        setThresholdSaving(true);
        try {
            const updated = await settingsApi.patch({ threshold_minutes: n });
            setSettings(updated);
            toastSuccess(`Seuil mis à jour à ${updated.webhook.threshold_minutes} minute(s).`);
        } catch (e) {
            toastError(e.message);
        } finally {
            setThresholdSaving(false);
        }
    };

    // ---- shipping card --------------------------------------------------------
    const toggleReveal = async (sc) => {
        if (revealed[sc.id]) {
            const next = { ...revealed };
            delete next[sc.id];
            setRevealed(next);
            return;
        }
        setRevealing(sc.id);
        try {
            const res = await shippingCompaniesApi.revealCredentials(sc.id);
            setRevealed((r) => ({ ...r, [sc.id]: res }));
            // Auto-remask after 15 s to limit clear-text exposure.
            setTimeout(() => {
                setRevealed((r) => { const n = { ...r }; delete n[sc.id]; return n; });
            }, 15000);
        } catch (e) {
            toastError(e.message);
        } finally {
            setRevealing(null);
        }
    };

    const onToggleActive = async (sc, next) => {
        try {
            await shippingCompaniesApi.update(sc.id, { is_active: next });
            toastSuccess(`${sc.name.toUpperCase()} ${next ? 'activé' : 'désactivé'}.`);
            await loadShipping();
        } catch (e) {
            toastError(e.message);
        }
    };

    const openCreate = () => { setDialogMode('create'); setDialogCompany(null); setDialogOpen(true); };
    const openEdit = (sc) => { setDialogMode('edit'); setDialogCompany(sc); setDialogOpen(true); };

    const submitDialog = async (payload) => {
        if (dialogMode === 'create') {
            await shippingCompaniesApi.create(payload);
            toastSuccess('Transporteur ajouté.');
        } else {
            await shippingCompaniesApi.update(dialogCompany.id, payload);
            toastSuccess('Transporteur mis à jour.');
        }
        setDialogOpen(false);
        await loadShipping();
    };

    const confirmDeleteNow = async () => {
        if (!confirmDelete) return;
        setBusy(true);
        try {
            await shippingCompaniesApi.remove(confirmDelete.sc.id);
            toastSuccess(`${confirmDelete.sc.name.toUpperCase()} supprimé.`);
            setConfirmDelete(null);
            await loadShipping();
        } catch (e) {
            toastError(e.message, { duration: 8000 });
            setConfirmDelete(null);
        } finally {
            setBusy(false);
        }
    };

    // ---- render ---------------------------------------------------------------
    const wh = settings?.webhook;

    return (
        <div data-testid="parametres-page" className="space-y-4">
            <div>
                <h2 className="text-2xl font-bold text-[#1A1D2E]">Paramètres</h2>
                <p className="text-sm text-[#6B7280]">Configurez la connexion Shopify, vos transporteurs et vos canaux WhatsApp.</p>
            </div>

            {/* ==================== CARD Whapi : Canaux WhatsApp (Phase 3) ==================== */}
            <WhatsappChannelsCard />

            {/* ==================== CARD Phase W2 : Webhooks Whapi (super_admin) ==================== */}
            <WhapiWebhooksCard />

            {/* ==================== CARD Phase W3.B1 : Nettoyer l'historique WhatsApp (super_admin) ==================== */}
            <CleanupHistoryCard />

            {/* ==================== CARD Phase 5 : Journal d'audit ==================== */}
            <AuditLogCard />

            {/* ==================== CARD 1 : Connexion Shopify ==================== */}
            <div data-testid="shopify-card" className="bg-white rounded-xl border border-[#E5E7EB] shadow-[0_1px_2px_rgba(16,24,40,0.05)] p-5 space-y-4">
                <div className="flex items-center justify-between">
                    <h3 className="text-base font-semibold text-[#1A1D2E]">Connexion Shopify</h3>
                    {wh && (
                        <span
                            data-testid="shopify-health-badge"
                            className={
                                'inline-flex items-center gap-1.5 h-7 px-3 rounded-full text-[12px] font-semibold ' +
                                (wh.healthy
                                    ? 'bg-[#10B981]/15 text-[#10B981]'
                                    : 'bg-[#EF4444]/15 text-[#EF4444]')
                            }
                        >
                            <span className="w-2 h-2 rounded-full" style={{ backgroundColor: wh.healthy ? '#10B981' : '#EF4444' }} />
                            {wh.healthy ? 'Opérationnel' : 'Inopérationnel — Vérifiez votre configuration Shopify'}
                        </span>
                    )}
                </div>

                {settingsLoading && !settings && (
                    <div data-testid="settings-loading" className="text-sm text-[#6B7280]">Chargement…</div>
                )}
                {settingsError && (
                    <div data-testid="settings-error" className="text-sm text-[#EF4444]">{settingsError}</div>
                )}

                {wh && (
                    <div className="grid md:grid-cols-2 gap-4">
                        <FormField label="URL du webhook Nivo">
                            <div className="flex items-center gap-2">
                                <input
                                    data-testid="shopify-webhook-url"
                                    readOnly
                                    value={wh.url}
                                    className="h-10 px-3 rounded-lg border border-[#E5E7EB] bg-[#F5F6FA] text-sm text-[#1A1D2E] flex-1 font-mono"
                                    onFocus={(e) => e.target.select()}
                                />
                                <button
                                    type="button"
                                    data-testid="shopify-webhook-copy"
                                    onClick={copyWebhookUrl}
                                    className="h-10 w-10 inline-flex items-center justify-center rounded-lg border border-[#E5E7EB] bg-white hover:bg-[#F5F6FA] text-[#6B7280]"
                                    aria-label="Copier l'URL du webhook"
                                    title="Copier"
                                >
                                    <Copy size={16} />
                                </button>
                            </div>
                        </FormField>

                        <FormField label="Dernière commande reçue">
                            <div
                                data-testid="shopify-last-order"
                                className="h-10 px-3 rounded-lg border border-[#E5E7EB] bg-[#F5F6FA] text-sm text-[#1A1D2E] flex items-center"
                            >
                                {formatFR(wh.latest_order_created_at)}
                            </div>
                        </FormField>

                        <FormField label={`Seuil d'alerte (minutes) — ${wh.threshold_min} à ${wh.threshold_max}`}>
                            <div className="flex items-center gap-2">
                                <input
                                    data-testid="shopify-threshold-input"
                                    type="number"
                                    min={wh.threshold_min}
                                    max={wh.threshold_max}
                                    step="1"
                                    value={thresholdInput}
                                    onChange={(e) => setThresholdInput(e.target.value)}
                                    className="h-10 px-3 rounded-lg border border-[#E5E7EB] bg-white text-sm text-[#1A1D2E] w-32 focus:outline-none focus:ring-2 focus:ring-[#4B3FD8]/25"
                                />
                                <button
                                    type="button"
                                    data-testid="shopify-threshold-save"
                                    onClick={saveThreshold}
                                    disabled={thresholdSaving || String(wh.threshold_minutes) === String(thresholdInput)}
                                    className="h-10 px-4 rounded-lg bg-gradient-to-r from-[#4B3FD8] to-[#9B7FF4] text-white text-sm font-semibold disabled:opacity-40"
                                >
                                    {thresholdSaving ? 'Enregistrement…' : 'Enregistrer'}
                                </button>
                            </div>
                        </FormField>
                    </div>
                )}
            </div>

            {/* ==================== CARD 1.5 : Diagnostic webhook (Phase 11.4) ==================== */}
            <ShopifyDiagnosticCard
                diag={diag}
                loading={diagLoading}
                open={diagOpen}
                onToggle={() => setDiagOpen(v => !v)}
                onRefresh={loadDiag}
            />

            {/* ==================== CARD 2 : Transporteurs ==================== */}
            <div data-testid="shipping-card" className="bg-white rounded-xl border border-[#E5E7EB] shadow-[0_1px_2px_rgba(16,24,40,0.05)]">
                <div className="p-5 border-b border-[#E5E7EB] flex items-center justify-between">
                    <h3 className="text-base font-semibold text-[#1A1D2E]">Transporteurs</h3>
                    <span className="text-xs text-[#6B7280]">Identifiants chiffrés (Fernet)</span>
                </div>
                <div className="overflow-x-auto">
                    <table data-testid="shipping-table" className="w-full min-w-[720px] text-sm">
                        <thead>
                            <tr className="bg-[#F5F6FA] text-[11px] uppercase tracking-wider text-[#6B7280]">
                                <Th>Transporteur</Th>
                                <Th>API Key</Th>
                                <Th>API Secret</Th>
                                <Th>Statut</Th>
                                <Th className="text-right">Actions</Th>
                            </tr>
                        </thead>
                        <tbody>
                            {shippingLoading && (<tr><td colSpan={5} className="text-center py-8 text-[#6B7280]" data-testid="shipping-loading">Chargement…</td></tr>)}
                            {!shippingLoading && shippingError && (<tr><td colSpan={5} className="text-center py-8 text-[#EF4444]">{shippingError}</td></tr>)}
                            {!shippingLoading && !shippingError && companies.length === 0 && (<tr><td colSpan={5} className="text-center py-8 text-[#6B7280]">Aucun transporteur — ajoutez-en un.</td></tr>)}
                            {!shippingLoading && !shippingError && companies.map((sc, idx) => {
                                const revealedCreds = revealed[sc.id];
                                return (
                                    <tr
                                        key={sc.id}
                                        data-testid="shipping-row"
                                        data-shipping-id={sc.id}
                                        data-shipping-name={sc.name}
                                        className={idx % 2 === 0 ? 'bg-white' : 'bg-[#F5F6FA]'}
                                    >
                                        <Td className="font-semibold text-[#1A1D2E] uppercase" data-testid="shipping-row-name">{sc.name}</Td>
                                        <Td data-testid="shipping-row-api-key" className="font-mono text-[12px]">
                                            <CredentialCell has={sc.has_api_key} value={revealedCreds?.api_key} />
                                        </Td>
                                        <Td data-testid="shipping-row-api-secret" className="font-mono text-[12px]">
                                            <CredentialCell has={sc.has_api_secret} value={revealedCreds?.api_secret} />
                                        </Td>
                                        <Td>
                                            <div className="flex items-center gap-3">
                                                <span
                                                    data-testid="shipping-row-status-pill"
                                                    className={
                                                        'inline-flex items-center h-6 px-2 rounded-md text-[11px] font-semibold ' +
                                                        (sc.is_active ? 'bg-[#10B981]/15 text-[#10B981]' : 'bg-[#EF4444]/15 text-[#EF4444]')
                                                    }
                                                >
                                                    {sc.is_active ? 'ACTIF' : 'NON ACTIF'}
                                                </span>
                                                <Switch
                                                    data-testid="shipping-row-toggle"
                                                    checked={sc.is_active}
                                                    onCheckedChange={(v) => onToggleActive(sc, v)}
                                                    className="data-[state=checked]:bg-[#10B981] data-[state=unchecked]:bg-[#E5E7EB]"
                                                />
                                            </div>
                                        </Td>
                                        <Td className="text-right">
                                            <div className="inline-flex items-center gap-1">
                                                <button
                                                    data-testid="shipping-row-reveal"
                                                    onClick={() => toggleReveal(sc)}
                                                    disabled={revealing === sc.id || (!sc.has_api_key && !sc.has_api_secret)}
                                                    className="h-8 w-8 inline-flex items-center justify-center rounded hover:bg-[#F5F6FA] text-[#6B7280] hover:text-[#4B3FD8] disabled:opacity-30"
                                                    aria-label={revealedCreds ? 'Masquer les identifiants' : 'Afficher les identifiants'}
                                                    title={revealedCreds ? 'Masquer' : 'Afficher (15 s)'}
                                                >
                                                    {revealedCreds ? <EyeOff size={16} /> : <Eye size={16} />}
                                                </button>
                                                <button
                                                    data-testid="shipping-row-edit"
                                                    onClick={() => openEdit(sc)}
                                                    className="h-8 w-8 inline-flex items-center justify-center rounded hover:bg-[#F5F6FA] text-[#6B7280] hover:text-[#4B3FD8]"
                                                    aria-label="Modifier"
                                                >
                                                    <Pencil size={16} />
                                                </button>
                                                <button
                                                    data-testid="shipping-row-delete"
                                                    onClick={() => setConfirmDelete({ sc })}
                                                    className="h-8 w-8 inline-flex items-center justify-center rounded hover:bg-[#EF4444]/10 text-[#EF4444]"
                                                    aria-label="Supprimer"
                                                >
                                                    <Trash2 size={16} />
                                                </button>
                                            </div>
                                        </Td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
                <div className="p-4 border-t border-[#E5E7EB]">
                    <button
                        type="button"
                        data-testid="shipping-add-button"
                        onClick={openCreate}
                        className="inline-flex items-center gap-1.5 text-[#4B3FD8] text-sm font-semibold hover:underline"
                    >
                        <Plus size={16} strokeWidth={2} />
                        Ajouter un transporteur
                    </button>
                </div>
            </div>

            <ShippingCompanyDialog
                open={dialogOpen}
                onClose={() => setDialogOpen(false)}
                mode={dialogMode}
                company={dialogCompany}
                onSubmit={submitDialog}
            />

            <AlertDialog open={!!confirmDelete} onOpenChange={(o) => { if (!o) setConfirmDelete(null); }}>
                <AlertDialogContent data-testid="shipping-delete-dialog" className="bg-white">
                    <AlertDialogHeader>
                        <AlertDialogTitle className="text-[#1A1D2E]">Supprimer ce transporteur ?</AlertDialogTitle>
                        <AlertDialogDescription className="text-[#6B7280]">
                            {confirmDelete ? `Êtes-vous sûr de vouloir supprimer ${confirmDelete.sc.name.toUpperCase()} ? Les commandes historiques conservent leur transporteur mais aucun nouvel envoi ne sera possible avec cette configuration.` : ''}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel disabled={busy} data-testid="shipping-delete-cancel">Annuler</AlertDialogCancel>
                        <AlertDialogAction
                            data-testid="shipping-delete-confirm"
                            disabled={busy}
                            onClick={confirmDeleteNow}
                            className="bg-[#EF4444] text-white hover:bg-[#DC2626] focus:ring-[#EF4444]"
                        >
                            Supprimer
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}

function CredentialCell({ has, value }) {
    if (!has) return <span className="text-[#6B7280]">—</span>;
    if (value) return <span className="text-[#1A1D2E] break-all">{value}</span>;
    return <span className="text-[#6B7280] tracking-widest">••••••••</span>;
}

function Th({ children, className = '' }) {
    return <th className={`text-left px-3 py-2 font-semibold ${className}`}>{children}</th>;
}
function Td({ children, className = '', ...rest }) {
    return <td className={`px-3 py-3 text-[#1A1D2E] align-middle ${className}`} {...rest}>{children}</td>;
}
function FormField({ label, children }) {
    return (
        <label className="flex flex-col gap-1">
            <span className="text-[12px] uppercase tracking-wider text-[#6B7280] font-semibold">{label}</span>
            {children}
        </label>
    );
}


// -----------------------------------------------------------------------
// Phase 3 (Whapi) — Admin card: 3 Whapi channels list.
// Phase 5 — Real QR pairing modal (mocked in preview), health-timeline
// widget under the card, and audit-log page (separate section below).
// -----------------------------------------------------------------------
import { channelsApi, auditApi, healthApi } from '../lib/messagerieApi';
import { usersApi } from '../lib/usersApi';

// -------------------------------------------------------------------------
// Phase W2 — « Synchroniser les webhooks Whapi » (super_admin uniquement).
// -------------------------------------------------------------------------
function WhapiWebhooksCard() {
    const [me, setMe] = React.useState(null);
    const [channels, setChannels] = React.useState(null);
    const [diagByChannel, setDiagByChannel] = React.useState({});
    const [busy, setBusy] = React.useState(null);
    const [error, setError] = React.useState(null);

    React.useEffect(() => {
        (async () => {
            try {
                const base = process.env.REACT_APP_BACKEND_URL;
                const [meRes, chRes] = await Promise.all([
                    fetch(`${base}/api/auth/me`, { credentials: 'include' }).then((r) => r.ok ? r.json() : null),
                    fetch(`${base}/api/whatsapp-channels`, { credentials: 'include' }).then((r) => r.ok ? r.json() : null),
                ]);
                setMe(meRes);
                if (chRes?.items) setChannels(chRes.items);
            } catch (_) { /* silent */ }
        })();
    }, []);

    const diagnose = async (channel_id) => {
        setBusy(channel_id);
        setError(null);
        try {
            const base = process.env.REACT_APP_BACKEND_URL;
            const res = await fetch(
                `${base}/api/whatsapp-channels/${channel_id}/whapi-settings`,
                { credentials: 'include' },
            );
            if (!res.ok) {
                const payload = await res.json().catch(() => ({}));
                throw new Error(payload.detail || `Erreur ${res.status}`);
            }
            const d = await res.json();
            setDiagByChannel((prev) => ({ ...prev, [channel_id]: d }));
        } catch (e) {
            setError(e.message || 'Diagnostic impossible');
        } finally {
            setBusy(null);
        }
    };

    const sync = async (channel_id) => {
        if (!window.confirm(`Synchroniser les webhooks Whapi du canal ${channel_id} ?\n\nL'URL, le mode et les headers (dont le secret) seront préservés à l'identique. Seule la liste d'events sera remplacée par la liste cible Nivo.`)) {
            return;
        }
        setBusy(channel_id);
        setError(null);
        try {
            const base = process.env.REACT_APP_BACKEND_URL;
            const res = await fetch(
                `${base}/api/whatsapp-channels/${channel_id}/whapi-settings/sync`,
                { method: 'POST', credentials: 'include' },
            );
            if (!res.ok) {
                const payload = await res.json().catch(() => ({}));
                throw new Error(payload.detail || `Erreur ${res.status}`);
            }
            toastSuccess('Webhooks Whapi synchronisés.');
            await diagnose(channel_id);
        } catch (e) {
            toastError(e.message || 'Synchronisation impossible');
            setError(e.message || 'Synchronisation impossible');
        } finally {
            setBusy(null);
        }
    };

    if (me && me.role !== 'super_admin') return null;

    return (
        <div
            data-testid="whapi-webhooks-card"
            className="bg-white rounded-xl border border-[#E5E7EB] shadow-[0_1px_2px_rgba(16,24,40,0.05)] p-5 space-y-4"
        >
            <div>
                <h3 className="text-base font-semibold text-[#1A1D2E]">Webhooks Whapi</h3>
                <p className="text-xs text-[#6B7280]">
                    Vérifier et aligner la liste d'events configurés chez Whapi pour chaque canal.
                    URL, mode et headers (secret inclus) sont préservés à l'identique.
                </p>
            </div>
            {error ? (
                <div className="text-sm text-[#EF4444]" data-testid="whapi-webhooks-error">{error}</div>
            ) : null}
            <div className="space-y-3">
                {(channels || []).map((ch) => {
                    const d = diagByChannel[ch.channel_id];
                    const isBusy = busy === ch.channel_id;
                    return (
                        <div
                            key={ch.channel_id}
                            data-testid={`whapi-webhooks-channel-${ch.channel_id}`}
                            className="border border-[#E5E7EB] rounded-lg p-3 space-y-2"
                        >
                            <div className="flex items-center justify-between gap-3">
                                <div className="text-sm font-medium text-[#1A1D2E]">
                                    {ch.channel_id}
                                    {ch.commercial_name ? ` · ${ch.commercial_name}` : ''}
                                </div>
                                <div className="flex items-center gap-2">
                                    <button
                                        type="button"
                                        data-testid={`whapi-webhooks-diagnose-${ch.channel_id}`}
                                        disabled={isBusy}
                                        onClick={() => diagnose(ch.channel_id)}
                                        className="h-8 px-3 rounded-md text-xs border border-[#E5E7EB] bg-white text-[#1A1D2E] hover:bg-[#F5F6FA] disabled:opacity-50"
                                    >
                                        {isBusy ? 'Patientez…' : 'Diagnostiquer'}
                                    </button>
                                    <button
                                        type="button"
                                        data-testid={`whapi-webhooks-sync-${ch.channel_id}`}
                                        disabled={isBusy || !d}
                                        onClick={() => sync(ch.channel_id)}
                                        className="h-8 px-3 rounded-md text-xs text-white bg-[#4B3FD8] hover:bg-[#3a2fb8] disabled:opacity-50"
                                        title={!d ? 'Diagnostiquer d\u2019abord' : 'Appliquer la liste cible d\u2019events'}
                                    >
                                        Synchroniser
                                    </button>
                                </div>
                            </div>
                            {d ? (
                                <div className="text-xs text-[#6B7280] space-y-1">
                                    <div>
                                        <span className="font-medium">État : </span>
                                        {d.in_sync ? (
                                            <span className="text-[#10B981]">Aligné</span>
                                        ) : (
                                            <span className="text-[#F59E0B]">Désaligné</span>
                                        )}
                                    </div>
                                    {d.missing?.length ? (
                                        <div>
                                            <span className="font-medium">Manquants :</span>{' '}
                                            {d.missing.join(', ')}
                                        </div>
                                    ) : null}
                                    {d.extra?.length ? (
                                        <div>
                                            <span className="font-medium">Supplémentaires :</span>{' '}
                                            {d.extra.join(', ')}
                                        </div>
                                    ) : null}
                                    {Array.isArray(d.webhooks) && d.webhooks.length ? (
                                        <div className="mt-1">
                                            <span className="font-medium">Webhook URL :</span>{' '}
                                            <code className="text-[11px]">{d.webhooks[0].url || '—'}</code>
                                            {' · '}
                                            <span>Headers : « •••• » (masqué)</span>
                                        </div>
                                    ) : null}
                                </div>
                            ) : null}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}


// -------------------------------------------------------------------------
// Phase W3.B1 — Carte « Nettoyer l'historique WhatsApp » (super_admin only).
// Jamais automatique : bouton Analyser (dry-run) puis bouton Lancer (run +
// polling /status). Backup intégral avant toute modif côté backend.
// -------------------------------------------------------------------------
function CleanupHistoryCard() {
    const [me, setMe] = React.useState(null);
    const [analyzing, setAnalyzing] = React.useState(false);
    const [analysis, setAnalysis] = React.useState(null);
    const [job, setJob] = React.useState(null);
    const [error, setError] = React.useState(null);
    const pollRef = React.useRef(null);

    React.useEffect(() => {
        (async () => {
            try {
                const base = process.env.REACT_APP_BACKEND_URL;
                const r = await fetch(`${base}/api/auth/me`, { credentials: 'include' });
                if (r.ok) setMe(await r.json());
                const s = await fetch(`${base}/api/admin/whatsapp-cleanup/status`,
                                       { credentials: 'include' });
                if (s.ok) {
                    const j = await s.json();
                    if (j?.job) setJob(j.job);
                }
            } catch (_) { /* silent */ }
        })();
    }, []);

    React.useEffect(() => {
        if (!job || job.status !== 'running') {
            if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
            return;
        }
        const tick = async () => {
            try {
                const base = process.env.REACT_APP_BACKEND_URL;
                const s = await fetch(`${base}/api/admin/whatsapp-cleanup/status`,
                                       { credentials: 'include' });
                if (s.ok) {
                    const j = await s.json();
                    if (j?.job) setJob(j.job);
                }
            } catch (_) { /* silent */ }
        };
        pollRef.current = setInterval(tick, 2000);
        return () => {
            if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [job?.status]);

    const analyze = async () => {
        setAnalyzing(true); setError(null); setAnalysis(null);
        try {
            const base = process.env.REACT_APP_BACKEND_URL;
            const r = await fetch(`${base}/api/admin/whatsapp-cleanup/analyze`,
                { method: 'POST', credentials: 'include' });
            if (!r.ok) {
                const p = await r.json().catch(() => ({}));
                throw new Error(p.detail || `Erreur ${r.status}`);
            }
            setAnalysis(await r.json());
        } catch (e) { setError(e.message || 'Analyse impossible'); }
        finally { setAnalyzing(false); }
    };

    const runCleanup = async () => {
        if (!window.confirm(
            "Lancer le nettoyage de l'historique WhatsApp ?\n\n" +
            "Cette opération :\n" +
            "• écrit un backup INTÉGRAL de chaque message modifié/supprimé " +
            "dans `messages_backup` AVANT toute action ;\n" +
            "• re-interroge Whapi pour chaque fantôme et applique la bonne action " +
            "(remplacement / dispatch / suppression) ;\n" +
            "• recalcule les previews et unread_count à la fin.\n\n" +
            "Elle est reprenable et non bloquante pour les webhooks live, " +
            "mais consomme des appels Whapi (throttle 5 req/s par canal)."
        )) return;
        setError(null);
        try {
            const base = process.env.REACT_APP_BACKEND_URL;
            const r = await fetch(`${base}/api/admin/whatsapp-cleanup/run`,
                { method: 'POST', credentials: 'include' });
            if (!r.ok) {
                const p = await r.json().catch(() => ({}));
                throw new Error(p.detail || `Erreur ${r.status}`);
            }
            const res = await r.json();
            const s = await fetch(`${base}/api/admin/whatsapp-cleanup/status`,
                                    { credentials: 'include' });
            if (s.ok) {
                const j = await s.json();
                if (j?.job) setJob(j.job);
            } else if (res.job) {
                setJob(res.job);
            }
            toastSuccess(res.status === 'already_running'
                ? 'Un nettoyage est déjà en cours.'
                : 'Nettoyage démarré.');
        } catch (e) {
            setError(e.message || 'Démarrage impossible');
            toastError(e.message || 'Démarrage impossible');
        }
    };

    if (me && me.role !== 'super_admin') return null;

    const progress = job?.progress || {};
    const summary = job?.summary || {};
    const processedPct = progress.total
        ? Math.round((progress.processed || 0) * 100 / progress.total)
        : 0;

    return (
        <div
            data-testid="cleanup-history-card"
            className="bg-white rounded-xl border border-[#E5E7EB] shadow-[0_1px_2px_rgba(16,24,40,0.05)] p-5 space-y-4"
        >
            <div>
                <h3 className="text-base font-semibold text-[#1A1D2E]">
                    Nettoyer l'historique WhatsApp
                </h3>
                <p className="text-xs text-[#6B7280]">
                    Outil ponctuel : détecte et corrige les messages fantômes pré-W2
                    (« [Message vide] », « [Type non supporté: action] », wrappers album…).
                    Ré-interroge Whapi, applique les actions sur leurs cibles réelles, et
                    supprime les restes. Backup intégral avant toute modification.
                </p>
            </div>
            {error ? (
                <div className="text-sm text-[#EF4444]" data-testid="cleanup-error">{error}</div>
            ) : null}
            <div className="flex items-center gap-2">
                <button
                    type="button"
                    data-testid="cleanup-analyze-btn"
                    disabled={analyzing || (job && job.status === 'running')}
                    onClick={analyze}
                    className="h-9 px-3 rounded-md text-sm border border-[#E5E7EB] bg-white text-[#1A1D2E] hover:bg-[#F5F6FA] disabled:opacity-50"
                >
                    {analyzing ? 'Analyse en cours…' : 'Analyser (dry-run)'}
                </button>
                <button
                    type="button"
                    data-testid="cleanup-run-btn"
                    disabled={(job && job.status === 'running')}
                    onClick={runCleanup}
                    className="h-9 px-3 rounded-md text-sm text-white bg-[#EF4444] hover:bg-[#dc2626] disabled:opacity-50"
                >
                    {job?.status === 'running' ? 'Nettoyage en cours…' : 'Lancer le nettoyage'}
                </button>
            </div>
            {analysis ? (
                <div
                    data-testid="cleanup-analysis"
                    className="text-xs text-[#6B7280] space-y-2 border border-[#E5E7EB] rounded-lg p-3"
                >
                    <div className="font-medium text-[#1A1D2E]">Résultat de l'analyse</div>
                    <div>
                        <span className="font-medium">Total par canal :</span>
                        <ul className="list-disc list-inside">
                            {Object.entries(analysis.totals_by_channel || {}).map(([ch, n]) => (
                                <li key={ch}>{ch} : {n} fantôme(s)</li>
                            ))}
                            {!Object.keys(analysis.totals_by_channel || {}).length && (
                                <li className="italic">Aucun fantôme détecté 🎉</li>
                            )}
                        </ul>
                    </div>
                    {analysis.top_affected_conversations?.length ? (
                        <div>
                            <span className="font-medium">Conversations les plus affectées :</span>
                            <ul className="list-disc list-inside">
                                {analysis.top_affected_conversations.map((c) => (
                                    <li key={c.conversation_id}>
                                        {c.contact_name || c.wa_id} —
                                        {' '}{c.ghost_count} fantôme(s), unread actuel : {c.current_unread}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    ) : null}
                </div>
            ) : null}
            {job ? (
                <div
                    data-testid="cleanup-job-status"
                    className="text-xs text-[#6B7280] space-y-2 border border-[#E5E7EB] rounded-lg p-3"
                >
                    <div className="font-medium text-[#1A1D2E]">
                        Job de nettoyage —
                        {' '}<span data-testid="cleanup-job-state">{job.status}</span>
                    </div>
                    {progress.total ? (
                        <>
                            <div className="w-full h-2 bg-[#E5E7EB] rounded-full overflow-hidden">
                                <div
                                    className="h-full bg-[#4B3FD8]"
                                    style={{ width: `${Math.min(100, processedPct)}%` }}
                                    data-testid="cleanup-progress-bar"
                                />
                            </div>
                            <div>
                                {progress.processed || 0} / {progress.total} fantômes traités
                                {' '}({processedPct}%)
                            </div>
                        </>
                    ) : null}
                    <div>
                        Supprimés : {summary.removed || 0} · Remplacés : {summary.requoted || 0} ·
                        {' '}Backup : {summary.backed_up || 0} · Erreurs : {progress.errors || 0}
                    </div>
                </div>
            ) : null}
        </div>
    );
}



function WhatsappChannelsCard() {
    const [channels, setChannels] = React.useState(null);
    const [agents, setAgents] = React.useState([]);
    const [saving, setSaving] = React.useState(null);
    const [error, setError] = React.useState(null);
    // Phase 5 — pairing modal state.
    const [pairingChannel, setPairingChannel] = React.useState(null);

    const load = React.useCallback(async () => {
        try {
            const res = await channelsApi.list();
            setChannels(res.items);
        } catch (e) {
            setError(e?.detail || e?.message || 'Erreur');
        }
    }, []);
    React.useEffect(() => { load(); }, [load]);
    // Auto-refresh la liste toutes les 30s pour que les transitions de
    // statut détectées par le monitor apparaissent quasi-instantanément.
    React.useEffect(() => {
        const iv = setInterval(load, 30000);
        return () => clearInterval(iv);
    }, [load]);
    // Récupère tous les cc_agents actifs pour le dropdown d'assignation.
    React.useEffect(() => {
        (async () => {
            try {
                const res = await usersApi.list();
                setAgents((res.users || []).filter((u) => u.role === 'cc_agent' && u.is_active));
            } catch { /* silent */ }
        })();
    }, []);

    const patch = async (channel_id, body) => {
        setSaving(channel_id);
        try {
            await channelsApi.patch(channel_id, body);
            await load();
            toastSuccess('Canal mis à jour.');
        } catch (e) {
            toastError(e?.detail || e?.message || 'Mise à jour impossible');
        } finally {
            setSaving(null);
        }
    };

    const badgeFor = (status) => {
        const map = {
            connected: { color: '#10B981', label: 'Connecté' },
            qr_required: { color: '#F59E0B', label: 'QR requis' },
            disconnected: { color: '#EF4444', label: 'Déconnecté' },
        };
        const c = map[status] || { color: '#9CA3AF', label: status || 'Inconnu' };
        return (
            <span
                data-testid="channel-status-badge"
                data-status={status}
                className="inline-flex items-center gap-1.5 h-6 px-2 rounded-full text-[11px] font-medium"
                style={{ backgroundColor: c.color + '26', color: c.color }}
            >
                <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: c.color }} />
                {c.label}
            </span>
        );
    };

    return (
        <div
            data-testid="whatsapp-channels-card"
            className="bg-white rounded-xl border border-[#E5E7EB] shadow-[0_1px_2px_rgba(16,24,40,0.05)] p-5 space-y-4"
        >
            <div className="flex items-center justify-between">
                <div>
                    <h3 className="text-base font-semibold text-[#1A1D2E]">Canaux WhatsApp</h3>
                    <p className="text-xs text-[#6B7280]">Un canal Whapi = un numéro WhatsApp géré par un agent.</p>
                </div>
            </div>
            {error ? <div className="text-sm text-[#EF4444]" data-testid="channels-error">{error}</div> : null}
            <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                    <thead className="bg-[#F5F6FA] text-[11px] uppercase tracking-wider text-[#6B7280]">
                        <tr>
                            <Th>Canal</Th><Th>Nom commercial</Th><Th>Numéro</Th>
                            <Th>Agent assigné</Th><Th>Statut</Th><Th>Vu</Th><Th className="text-right">Actions</Th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-[#F3F4F6]">
                        {(channels || []).map((c) => (
                            <tr key={c.channel_id} data-testid="channel-row" data-channel-id={c.channel_id}>
                                <Td className="font-mono font-semibold">{c.channel_id}</Td>
                                <CommercialNameCell
                                    channel={c}
                                    saving={saving === c.channel_id}
                                    onSave={(val) => patch(c.channel_id, { commercial_name: val })}
                                />
                                <Td className="font-mono text-[12px]">{c.phone_number || '—'}</Td>
                                <Td>
                                    <select
                                        data-testid="channel-agent-select"
                                        value={c.agent_user_id || ''}
                                        disabled={saving === c.channel_id}
                                        onChange={(e) => patch(c.channel_id, { agent_user_id: e.target.value || null })}
                                        className="h-7 px-2 text-[12px] rounded border border-[#E5E7EB] bg-white text-[#1A1D2E] focus:outline-none focus:border-[#4B3FD8]"
                                    >
                                        <option value="">— Aucun —</option>
                                        {agents.map((u) => (
                                            <option key={u.id} value={u.id}>
                                                {u.display_name || u.email}
                                            </option>
                                        ))}
                                    </select>
                                </Td>
                                <Td>{badgeFor(c.status)}</Td>
                                <Td className="text-[11px] text-[#6B7280]">
                                    {c.last_seen ? formatFR(c.last_seen) : '—'}
                                </Td>
                                <Td className="text-right">
                                    <button
                                        type="button"
                                        data-testid="channel-repair-btn"
                                        onClick={() => setPairingChannel(c)}
                                        title={c.status === 'connected' ? 'Ré-appairer ce canal' : 'Appairer ce canal'}
                                        className="h-7 px-2.5 rounded-md border border-[#4B3FD8] text-[11px] text-[#4B3FD8] bg-white hover:bg-[#4B3FD8]/5"
                                    >
                                        {c.status === 'connected' ? 'Ré-appairer' : 'Appairer'}
                                    </button>
                                </Td>
                            </tr>
                        ))}
                        {channels && channels.length === 0 && (
                            <tr><Td colSpan={7} className="text-center text-[#6B7280]">Aucun canal configuré.</Td></tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* Phase 5 — Timeline santé 24h */}
            <ChannelHealthTimeline />

            {pairingChannel ? (
                <PairingModal
                    channel={pairingChannel}
                    onClose={() => { setPairingChannel(null); load(); }}
                />
            ) : null}
        </div>
    );
}

// -----------------------------------------------------------------------
// Phase 5.1 — Édition inline du nom commercial dans la table des canaux.
// Pattern cohérent avec le dropdown "Agent assigné" : la fonction `onSave`
// est le `patch` existant du parent (toast + reload gérés là-bas), l'audit
// log serveur existe déjà via `_write_audit_log` sur `channel_patch`.
// RBAC : la route `/parametres` est déjà réservée au SUPER_ADMIN
// (routeConfig.js:55), aucune garde supplémentaire nécessaire ici.
// -----------------------------------------------------------------------
function CommercialNameCell({ channel, saving, onSave }) {
    const [editing, setEditing] = React.useState(false);
    const [draft, setDraft] = React.useState(channel.commercial_name || '');
    const inputRef = React.useRef(null);

    React.useEffect(() => {
        // Sync when the parent reloads the channel list after a save.
        if (!editing) setDraft(channel.commercial_name || '');
    }, [channel.commercial_name, editing]);

    const begin = () => {
        setDraft(channel.commercial_name || '');
        setEditing(true);
        setTimeout(() => inputRef.current && inputRef.current.focus(), 0);
    };
    const cancel = () => { setEditing(false); setDraft(channel.commercial_name || ''); };
    const commit = async () => {
        const normalized = draft.trim();
        // No-op si inchangé — évite un PATCH inutile et une entrée audit vide.
        if (normalized === (channel.commercial_name || '')) { setEditing(false); return; }
        // La valeur vide est acceptée (le backend normalise None → '—' à l'affichage).
        await onSave(normalized || null);
        setEditing(false);
    };
    const onKey = (e) => {
        if (e.key === 'Enter') { e.preventDefault(); commit(); }
        else if (e.key === 'Escape') { e.preventDefault(); cancel(); }
    };

    if (!editing) {
        return (
            <Td>
                <div className="group inline-flex items-center gap-1.5">
                    <span data-testid="channel-commercial-name" className="text-sm text-[#1A1D2E]">
                        {channel.commercial_name || '—'}
                    </span>
                    <button
                        type="button"
                        data-testid="channel-commercial-name-edit"
                        onClick={begin}
                        title="Modifier le nom commercial"
                        aria-label="Modifier le nom commercial"
                        className="text-[#9CA3AF] hover:text-[#4B3FD8] focus:text-[#4B3FD8] transition-colors p-0.5 rounded"
                    >
                        <Pencil size={12} strokeWidth={1.75} />
                    </button>
                </div>
            </Td>
        );
    }
    return (
        <Td>
            <div className="inline-flex items-center gap-1">
                <input
                    ref={inputRef}
                    data-testid="channel-commercial-name-input"
                    type="text"
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={onKey}
                    disabled={saving}
                    maxLength={100}
                    placeholder="— vide —"
                    className="h-7 px-2 text-sm w-[140px] rounded border border-[#E5E7EB] bg-white text-[#1A1D2E] focus:outline-none focus:border-[#4B3FD8]"
                />
                <button
                    type="button"
                    data-testid="channel-commercial-name-save"
                    onClick={commit}
                    disabled={saving}
                    title="Valider (Entrée)"
                    aria-label="Valider"
                    className="h-7 w-7 inline-flex items-center justify-center rounded border border-[#4B3FD8] text-[#4B3FD8] bg-white hover:bg-[#4B3FD8]/5 disabled:opacity-50"
                >
                    <Check size={13} strokeWidth={2} />
                </button>
                <button
                    type="button"
                    data-testid="channel-commercial-name-cancel"
                    onClick={cancel}
                    disabled={saving}
                    title="Annuler (Échap)"
                    aria-label="Annuler"
                    className="h-7 w-7 inline-flex items-center justify-center rounded border border-[#E5E7EB] text-[#6B7280] bg-white hover:bg-[#F5F6FA] disabled:opacity-50"
                >
                    <X size={13} strokeWidth={2} />
                </button>
            </div>
        </Td>
    );
}


// -----------------------------------------------------------------------
// Phase 5 — Modal QR pairing (polling 2s).
// Rationale polling vs WebSocket : l'ingress Kubernetes peut bufferiser
// les WS ; le polling 2s reproduit le pattern bridge_status existant et
// répond en < 2 s dès que le worker Whapi (via webhook) flippe le statut.
// -----------------------------------------------------------------------
function PairingModal({ channel, onClose }) {
    const [session, setSession] = React.useState(null);
    const [status, setStatus] = React.useState('starting');
    const [error, setError] = React.useState(null);
    const [phone, setPhone] = React.useState(null);
    const [elapsed, setElapsed] = React.useState(0);
    const startedAtRef = React.useRef(Date.now());

    // Kick off pairing on open.
    React.useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const res = await channelsApi.startPairing(channel.channel_id);
                if (cancelled) return;
                setSession(res);
                setStatus(res.status || 'qr_required');
            } catch (e) {
                if (!cancelled) setError(e?.detail || e?.message || 'Impossible de démarrer le pairing');
            }
        })();
        return () => { cancelled = true; };
    }, [channel.channel_id]);

    // Poll every 2s while not yet connected. Stops after 90s → status "expired".
    React.useEffect(() => {
        if (!session) return;
        if (status === 'connected' || status === 'expired' || status === 'error') return;
        let cancelled = false;
        const tick = async () => {
            try {
                const res = await channelsApi.pairingStatus(channel.channel_id);
                if (cancelled) return;
                setPhone(res.phone_number || null);
                setStatus(res.status || 'qr_required');
                const spent = Math.floor((Date.now() - startedAtRef.current) / 1000);
                setElapsed(spent);
                if (spent >= 90 && res.status !== 'connected') {
                    setStatus('expired');
                }
            } catch (e) { /* keep last known status */ }
        };
        const iv = setInterval(tick, 2000);
        tick();
        return () => { cancelled = true; clearInterval(iv); };
    }, [session, status, channel.channel_id]);

    const restart = async () => {
        setSession(null);
        setStatus('starting');
        setError(null);
        setPhone(null);
        setElapsed(0);
        startedAtRef.current = Date.now();
        try {
            const res = await channelsApi.startPairing(channel.channel_id);
            setSession(res);
            setStatus(res.status || 'qr_required');
        } catch (e) {
            setError(e?.detail || e?.message || 'Impossible de démarrer le pairing');
        }
    };

    return (
        <div
            data-testid="pairing-modal"
            data-status={status}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
        >
            <div className="w-full max-w-md bg-white rounded-xl border border-[#E5E7EB] shadow-lg p-5 space-y-4">
                <div className="flex items-start justify-between">
                    <div>
                        <h4 className="text-base font-semibold text-[#1A1D2E]">
                            Appairer {channel.channel_id} {channel.commercial_name ? `(${channel.commercial_name})` : ''}
                        </h4>
                        <p className="text-xs text-[#6B7280]">
                            Scannez le QR depuis WhatsApp → Paramètres → Appareils connectés.
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        data-testid="pairing-modal-close"
                        aria-label="Fermer"
                        className="text-[#6B7280] hover:text-[#1A1D2E] text-xl leading-none px-1"
                    >
                        ×
                    </button>
                </div>
                {error ? (
                    <div data-testid="pairing-error" className="text-sm text-[#EF4444]">{error}</div>
                ) : null}
                {status === 'connected' ? (
                    <div data-testid="pairing-success" className="bg-[#10B981]/10 border border-[#10B981]/30 rounded-lg p-4 text-center">
                        <div className="text-[#10B981] font-semibold mb-1">✓ Canal appairé</div>
                        <div className="text-sm text-[#1A1D2E]">
                            {phone ? <>Numéro connecté : <span className="font-mono">{phone}</span></> : 'Session confirmée.'}
                        </div>
                    </div>
                ) : (
                    <div className="flex flex-col items-center gap-2">
                        <div
                            data-testid="pairing-qr"
                            className="w-56 h-56 rounded-lg border border-[#E5E7EB] bg-[#F5F6FA] flex items-center justify-center overflow-hidden"
                        >
                            {session?.qr_data_url ? (
                                <img
                                    src={session.qr_data_url}
                                    alt="QR de pairing"
                                    className="w-full h-full object-contain"
                                    style={{ imageRendering: 'pixelated' }}
                                />
                            ) : (
                                <span className="text-[11px] text-[#6B7280]">Génération du QR…</span>
                            )}
                        </div>
                        <div data-testid="pairing-status-label" className="text-[11px] text-[#6B7280]">
                            {status === 'expired'
                                ? 'QR expiré. Régénérez ci-dessous.'
                                : `En attente de scan… (${elapsed}s / 90s)`}
                        </div>
                        {status === 'expired' ? (
                            <button
                                type="button"
                                data-testid="pairing-restart"
                                onClick={restart}
                                className="h-8 px-3 rounded-md bg-[#4B3FD8] text-white text-xs font-medium"
                            >
                                Générer un nouveau QR
                            </button>
                        ) : null}
                    </div>
                )}
                <div className="flex items-center justify-end gap-2">
                    <button
                        type="button"
                        onClick={onClose}
                        data-testid="pairing-modal-done"
                        className="h-8 px-3 rounded-md border border-[#E5E7EB] text-xs text-[#1A1D2E]"
                    >
                        {status === 'connected' ? 'Terminer' : 'Fermer'}
                    </button>
                </div>
            </div>
        </div>
    );
}

// -----------------------------------------------------------------------
// Phase 5 — Timeline santé 24h (colorée par transition).
// -----------------------------------------------------------------------
function ChannelHealthTimeline() {
    const [events, setEvents] = React.useState([]);
    const [loading, setLoading] = React.useState(true);

    React.useEffect(() => {
        let cancelled = false;
        const load = async () => {
            try {
                const res = await healthApi.events({ since_hours: 24 });
                if (!cancelled) setEvents(res.items || []);
            } catch { /* silent */ }
            finally { if (!cancelled) setLoading(false); }
        };
        load();
        const iv = setInterval(load, 60000);
        return () => { cancelled = true; clearInterval(iv); };
    }, []);

    const colorFor = (status) => (
        status === 'connected' ? '#10B981'
            : status === 'qr_required' ? '#F59E0B'
            : status === 'disconnected' ? '#EF4444'
            : '#9CA3AF'
    );

    return (
        <div data-testid="channel-health-timeline" className="mt-4 border-t border-[#E5E7EB] pt-4">
            <div className="text-xs uppercase tracking-wide text-[#6B7280] font-medium mb-2">
                Événements santé — 24 dernières heures
            </div>
            {loading ? (
                <div className="text-xs text-[#6B7280]">Chargement…</div>
            ) : events.length === 0 ? (
                <div className="text-xs text-[#6B7280]" data-testid="health-timeline-empty">
                    Aucun événement santé dans les 24 dernières heures.
                </div>
            ) : (
                <ul className="space-y-1.5 max-h-48 overflow-y-auto">
                    {events.map((e, i) => (
                        <li
                            key={i}
                            data-testid="health-timeline-event"
                            data-channel-id={e.channel_id}
                            data-new-status={e.new_status}
                            className="flex items-center gap-2 text-[12px]"
                        >
                            <span
                                className="w-2 h-2 rounded-full shrink-0"
                                style={{ backgroundColor: colorFor(e.new_status) }}
                            />
                            <span className="font-mono font-semibold text-[#1A1D2E]">{e.channel_id}</span>
                            <span className="text-[#6B7280]">
                                {e.old_status} → <span style={{ color: colorFor(e.new_status), fontWeight: 500 }}>{e.new_status}</span>
                            </span>
                            {e.alert_sent ? (
                                <span className="text-[10px] px-1.5 rounded bg-[#4B3FD8]/10 text-[#4B3FD8] font-medium">alerte email</span>
                            ) : null}
                            <span className="ml-auto text-[10px] text-[#9CA3AF]">
                                {formatFR(e.recorded_at)}
                            </span>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}

// -----------------------------------------------------------------------
// Phase 5 — Journal d'audit (super_admin/manager).
// -----------------------------------------------------------------------
const AUDIT_ACTION_LABELS = {
    order_status_change: 'Statut de commande',
    order_reassign: 'Réassignation commande',
    conversation_reassign: 'Réassignation conversation',
    channel_patch: 'Canal WhatsApp modifié',
    channel_start_pairing: 'Pairing canal démarré',
    user_patch: 'Utilisateur modifié',
};

export function AuditLogCard() {
    const [rows, setRows] = React.useState([]);
    const [loading, setLoading] = React.useState(true);
    const [total, setTotal] = React.useState(0);
    const [filterAction, setFilterAction] = React.useState('');
    const [filterUser, setFilterUser] = React.useState('');
    const [dateFrom, setDateFrom] = React.useState('');
    const [dateTo, setDateTo] = React.useState('');
    const [users, setUsers] = React.useState([]);

    React.useEffect(() => {
        (async () => {
            try {
                const res = await usersApi.list();
                setUsers(res.users || []);
            } catch { /* silent */ }
        })();
    }, []);

    const load = React.useCallback(async () => {
        setLoading(true);
        try {
            const params = { page_size: 50 };
            if (filterAction) params.action_type = filterAction;
            if (filterUser) params.user_id = filterUser;
            if (dateFrom) params.date_from = new Date(dateFrom).toISOString();
            if (dateTo) params.date_to = new Date(dateTo).toISOString();
            const res = await auditApi.list(params);
            setRows(res.items || []);
            setTotal(res.total || 0);
        } catch (e) {
            toastError(e?.detail || e?.message || 'Chargement du journal impossible');
        } finally { setLoading(false); }
    }, [filterAction, filterUser, dateFrom, dateTo]);
    React.useEffect(() => { load(); }, [load]);

    return (
        <div
            data-testid="audit-log-card"
            className="bg-white rounded-xl border border-[#E5E7EB] shadow-[0_1px_2px_rgba(16,24,40,0.05)] p-5 space-y-4"
        >
            <div className="flex items-center justify-between">
                <div>
                    <h3 className="text-base font-semibold text-[#1A1D2E]">Journal d'audit</h3>
                    <p className="text-xs text-[#6B7280]">
                        Actions sensibles récentes ({total} entrée{total > 1 ? 's' : ''}).
                    </p>
                </div>
            </div>
            <div className="flex flex-wrap items-end gap-2">
                <div>
                    <label className="block text-[10px] uppercase tracking-wide text-[#6B7280]">Action</label>
                    <select
                        data-testid="audit-filter-action"
                        value={filterAction}
                        onChange={(e) => setFilterAction(e.target.value)}
                        className="h-7 px-2 text-xs rounded border border-[#E5E7EB] bg-white text-[#1A1D2E]"
                    >
                        <option value="">Toutes</option>
                        {Object.entries(AUDIT_ACTION_LABELS).map(([k, l]) => (
                            <option key={k} value={k}>{l}</option>
                        ))}
                    </select>
                </div>
                <div>
                    <label className="block text-[10px] uppercase tracking-wide text-[#6B7280]">Utilisateur</label>
                    <select
                        data-testid="audit-filter-user"
                        value={filterUser}
                        onChange={(e) => setFilterUser(e.target.value)}
                        className="h-7 px-2 text-xs rounded border border-[#E5E7EB] bg-white text-[#1A1D2E] max-w-[220px]"
                    >
                        <option value="">Tous</option>
                        {users.map((u) => (
                            <option key={u.id} value={u.id}>{u.display_name || u.email}</option>
                        ))}
                    </select>
                </div>
                <div>
                    <label className="block text-[10px] uppercase tracking-wide text-[#6B7280]">Du</label>
                    <input
                        type="date"
                        data-testid="audit-filter-from"
                        value={dateFrom}
                        onChange={(e) => setDateFrom(e.target.value)}
                        className="h-7 px-2 text-xs rounded border border-[#E5E7EB] bg-white text-[#1A1D2E]"
                    />
                </div>
                <div>
                    <label className="block text-[10px] uppercase tracking-wide text-[#6B7280]">Au</label>
                    <input
                        type="date"
                        data-testid="audit-filter-to"
                        value={dateTo}
                        onChange={(e) => setDateTo(e.target.value)}
                        className="h-7 px-2 text-xs rounded border border-[#E5E7EB] bg-white text-[#1A1D2E]"
                    />
                </div>
            </div>
            <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                    <thead className="bg-[#F5F6FA] text-[11px] uppercase tracking-wider text-[#6B7280]">
                        <tr>
                            <Th>Horodatage</Th><Th>Utilisateur</Th><Th>Action</Th><Th>Cible</Th><Th>Avant → Après</Th><Th>IP</Th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-[#F3F4F6]">
                        {loading ? (
                            <tr><Td colSpan={6} className="text-center text-[#6B7280]">Chargement…</Td></tr>
                        ) : rows.length === 0 ? (
                            <tr><Td colSpan={6} className="text-center text-[#6B7280]" data-testid="audit-empty">Aucun événement.</Td></tr>
                        ) : rows.map((r) => (
                            <tr key={r.id} data-testid="audit-row" data-action-type={r.action_type}>
                                <Td className="text-[11px] text-[#6B7280] whitespace-nowrap">{formatFR(r.timestamp)}</Td>
                                <Td className="text-[12px]">{r.user_email || '—'}</Td>
                                <Td>
                                    <span className="inline-flex items-center h-5 px-2 rounded text-[10px] font-medium bg-[#4B3FD8]/10 text-[#4B3FD8]">
                                        {AUDIT_ACTION_LABELS[r.action_type] || r.action_type}
                                    </span>
                                </Td>
                                <Td className="font-mono text-[11px] max-w-[160px] truncate" title={r.target_id}>
                                    {r.target_type}:{r.target_id}
                                </Td>
                                <Td className="font-mono text-[10px] text-[#6B7280] max-w-[300px] truncate">
                                    {JSON.stringify(r.before_state)} → {JSON.stringify(r.after_state)}
                                </Td>
                                <Td className="text-[11px] text-[#6B7280] font-mono">{r.ip_address || '—'}</Td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
